/** Side effects of the chooser: work queue, clip + template options, OK / skip / undo, URL reflection. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { api, newIdempotencyKey, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { errorText } from "../common";
import { parseIdList, useUrlState } from "../url";
import { cancelRender, chooseTemplates, disabledReason, skipClip, unskipClip } from "./api";
import { chooserReducer, initialChooser, phase } from "./machine";
import { chooseParams, stable, type Params, type ParamsByTemplate } from "./params";

export const workKey = (exclude: number[]) => qk.list("work-next", { queue: "template", exclude });

export function useChooser() {
  const url = useUrlState();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [s, dispatch] = useReducer(chooserReducer, undefined,
    () => initialChooser(parseIdList(url.str("exclude")), url.int("clip")));
  const [error, setError] = useState<unknown>(null);
  const busy = useRef(false);
  const keys = useRef(new Map<string, string>());
  // per-template knobs from the Edit drawers; kept across clips so one tweak can be applied to many clips
  const [params, setParamsState] = useState<ParamsByTemplate>({});
  const setParams = useCallback((templateId: string, p: Params) => {
    setParamsState((old) => ({ ...old, [templateId]: p }));
  }, []);

  const setUrl = url.set;
  useEffect(() => setUrl({ exclude: s.exclude.join(",") || null, clip: s.pinned }), [s.exclude, s.pinned, setUrl]);
  // a link to /choose?clip=<id> while on this page (the queue table, history) pins that clip (the PIN path)
  const urlClip = url.int("clip");
  const seenUrlClip = useRef(urlClip);
  const pinnedNow = useRef(s.pinned);
  pinnedNow.current = s.pinned;
  useEffect(() => {
    if (urlClip === seenUrlClip.current) return;
    seenUrlClip.current = urlClip;
    if (urlClip != null && urlClip !== pinnedNow.current) dispatch({ type: "PIN", clipId: urlClip });
  }, [urlClip]);

  const work = useQuery({
    queryKey: workKey(s.exclude),
    enabled: s.pinned == null,
    staleTime: 0,
    queryFn: () => unwrap(api.GET("/api/work/next", { params: { query: { queue: "template", exclude: s.exclude.join(",") } } })),
  });
  const clipId = s.pinned ?? (work.data?.none_left ? null : work.data?.item?.item_id ?? null);
  const clip = useQuery({
    queryKey: qk.entity("clip", clipId ?? 0),
    enabled: clipId != null,
    queryFn: () => unwrap(api.GET("/api/clips/{clip_id}", { params: { path: { clip_id: clipId as number } } })),
  });
  const options = useQuery({
    queryKey: qk.entity("clip", clipId ?? 0, "template-options"),
    enabled: clipId != null,
    queryFn: () => unwrap(api.GET("/api/clips/{clip_id}/template-options", { params: { path: { clip_id: clipId as number } } })),
  });

  const ph = phase({ pending: s.pinned == null && work.isPending, error: s.pinned == null && work.isError,
    noneLeft: s.pinned == null && !!work.data?.none_left, clipId });
  const opts = options.data ?? [];
  const focus = Math.min(s.focus, Math.max(0, opts.length - 1));

  // Undo window expiry.
  useEffect(() => {
    if (!s.undo) return;
    const t = setTimeout(() => dispatch({ type: "UNDO_EXPIRED", now: Date.now() }), Math.max(0, s.undo.until - Date.now()));
    return () => clearTimeout(t);
  }, [s.undo]);

  const refreshClip = useCallback((id: number | null) => {
    void qc.invalidateQueries({ queryKey: ["list", "work-next"] });
    if (id != null) void qc.invalidateQueries({ queryKey: qk.entity("clip", id) });
  }, [qc]);

  const toggle = (templateId: string) => {
    const o = opts.find((x) => x.template.id === templateId);
    if (!o) return;
    const why = disabledReason(o);
    if (why) {
      announce(`${o.template.title} cannot be chosen: ${why}`, "error");
      return;
    }
    dispatch({ type: "TOGGLE", templateId });
    announce(`${s.selected.includes(templateId) ? "Unselected" : "Selected"} ${o.template.title}`);
  };

  const chooseFocused = () => {
    const o = opts[focus];
    if (o) toggle(o.template.id);
  };

  /** OK: queue the selected (or highlighted) template for this clip and move on (one clip, one template). */
  const submit = async () => {
    if (busy.current || clipId == null) return;
    let sel = s.selected;
    if (sel.length === 0) {
      const o = opts[focus];
      if (o && !disabledReason(o)) {
        sel = [o.template.id];
        dispatch({ type: "TOGGLE", templateId: o.template.id });
      }
    }
    if (sel.length === 0) {
      announce("Choose a template first", "error");
      return;
    }
    busy.current = true;
    dispatch({ type: "SUBMIT" });
    const body = chooseParams(sel, params);
    const k = `${clipId}:${[...sel].sort().join(",")}:${stable(body)}`;
    const idem = keys.current.get(k) ?? newIdempotencyKey();
    keys.current.set(k, idem);
    try {
      const renders = await chooseTemplates(clipId, sel, idem, body);
      keys.current.delete(k);
      setError(null);
      const ids = renders.map((r) => r.id);
      dispatch({ type: "QUEUED", clipId, renderIds: ids, now: Date.now() });
      announce(`Queued render${ids.length === 1 ? "" : "s"} ${ids.map((i) => `#${i}`).join(", ")} for clip #${clipId}`);
      void qc.invalidateQueries({ queryKey: qk.entity("clip", clipId) });
      refreshClip(null);
    } catch (e) {
      dispatch({ type: "FAILED" });
      setError(e);
      announce(`OK failed: ${errorText(e)}`, "error");
    } finally {
      busy.current = false;
    }
  };

  const next = () => {
    if (clipId == null || s.submitting) return;
    setError(null);
    dispatch({ type: "NEXT_CLIP", clipId });
    announce(`Passed clip #${clipId}`);
  };

  const skip = async () => {
    const c = clip.data;
    if (!c || busy.current) return;
    busy.current = true;
    try {
      const out = await skipClip(c);
      setError(null);
      dispatch({ type: "SKIPPED", clipId: c.id, version: out.version, now: Date.now() });
      announce(`Skipped clip #${c.id}`);
      refreshClip(c.id);
    } catch (e) {
      setError(e);
      announce(`Skip failed: ${errorText(e)}`, "error");
    } finally {
      busy.current = false;
    }
  };

  const undo = async () => {
    const u = s.undo;
    if (!u || busy.current) return;
    busy.current = true;
    try {
      if (u.kind === "renders") {
        for (const r of u.renderIds) await cancelRender(r);
        announce(`Undone: cancelled render${u.renderIds.length === 1 ? "" : "s"} ${u.renderIds.map((r) => `#${r}`).join(", ")}`);
      } else {
        await unskipClip(u.clipId, u.version);
        announce(`Undone: clip #${u.clipId} needs a template again`);
      }
      setError(null);
      dispatch({ type: "UNDONE" });
      refreshClip(u.clipId);
      void qc.invalidateQueries({ queryKey: qk.entity("clip", u.clipId, "template-options") });
    } catch (e) {
      setError(e);
      announce(`Undo failed: ${errorText(e)}`, "error");
    } finally {
      busy.current = false;
    }
  };

  const restart = () => {
    dispatch({ type: "RESTART" });
    setError(null);
    announce("Starting over: passed clips are back in the queue");
  };

  /** Open a clip from the queue table (or a link): pinned, outside the queue order. */
  const pin = (id: number) => {
    if (s.submitting || id === clipId) return;
    setError(null);
    dispatch({ type: "PIN", clipId: id });
    announce(`Opened clip #${id}`);
  };

  const reload = () => {
    setError(null);
    refreshClip(clipId);
    if (clipId != null) void qc.invalidateQueries({ queryKey: qk.entity("clip", clipId, "template-options") });
  };

  return {
    s, phase: ph, clipId, work, clip, options, opts, focus, error, reload, params,
    actions: {
      toggle, chooseFocused, next, skip, undo, restart, submit, setParams, pin,
      focusBy: (d: number) => dispatch({ type: "FOCUS", index: Math.min(Math.max(0, focus + d), Math.max(0, opts.length - 1)) }),
      focusAt: (i: number) => dispatch({ type: "FOCUS", index: i }),
    },
  };
}
